import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "reducers";
import { IOutput } from "../../domain/IOutput";
import { IOutputListItemSearch } from "../../domain/IOutputListItemSearch";
import { stockService } from "../../services/stock.service";
import { ICreateReturnOutput } from "../../domain/ICreateReturnOutput";
import { ISingleItemModalProps } from "../../domain/ISingleItemModalProps";
import { IOutputItem } from "../../domain/IOutputItem";
import { IPopoverState } from "Dashboard/scenes/Carnets/domain/IPopoverState";
import { IOutputListItem } from "../../domain/IOutputListItem";
import { IStockOutputUpdate } from "../../domain/IStockOutputUpdate";
import { IGetPageRequestParams, IUpdateStateAfterSearchParams } from "hooks/pagingAndSort";
import { IUpdateOutputComand } from "../../domain/IUpdateOutputCommand";

export const getOutputListItems = createAsyncThunk(
  "Stock/getOutputListItems",
  async (params: {
    getOutputListItemsSearchParams: IOutputListItemSearch,
    pagingParams: IGetPageRequestParams,
    abortSignal: AbortSignal,
    updateStateAfterFetch: (
      updateStateParams: IUpdateStateAfterSearchParams
    ) => void
  }, { rejectWithValue }) => {
    try {
      const response = await stockService.getOutputs("Lager",
        params.getOutputListItemsSearchParams,
        params.pagingParams,
        params.abortSignal);
      return {
        response: response,
        pagingData: params.pagingParams,
        updateStateAfterFetch: params.updateStateAfterFetch
      }
    } catch (err) {
      setError(err);
    }
  }
);

export const getOutputForEdit = createAsyncThunk(
  "Stock/getOutputForEdit",
  async (outputId: number) => {
    try {
      const result = await stockService.getOutput(
        outputId,
      );
      return result;
    } catch (err) {
      setError(err);
    }
  }
);

export const getOutputForPrint = createAsyncThunk(
  "Stock/getOutputForPrint",
  async (outputId: number) => {
    try {
      const result = await stockService.getOutput(
        outputId,
      );
      return result;
    } catch (err) {
      setError(err);
    }
  }
);

export const returnOutput = createAsyncThunk(
  "Stock/returnOutput",
  async ({
    outputId,
    params,
  }: {
    outputId: number;
    params: ICreateReturnOutput;
  }, { rejectWithValue }) => {
    try {
      const result = await stockService.returnOutput(
        outputId,
        params
      );
      return result;
    } catch (err: any) {
      return rejectWithValue(err.response.status);
    }
  }
);

export const updateOutputItems = createAsyncThunk(
  "Stock/updateOutputItems",
  async (command: IUpdateOutputComand, { rejectWithValue }) => {
    try {
      const result = await stockService.updateOutputItems(command);
      return { request: command, response: result };
    } catch (err: any) {
      return rejectWithValue(err.response.status);
    }
  }
);

export const updateStockOutput = createAsyncThunk(
  "Stock/updateOutput",
  async (data: IStockOutputUpdate, { rejectWithValue }) => {
    try {
      return await stockService.updateStockOutput(data);
    } catch (err: any) {
      return rejectWithValue(err.response.status);
    }
  }
);

export const deleteStockOutput = createAsyncThunk(
  "Stock/deleteStockOutput",
  async (outputId: number, { rejectWithValue }) => {
    try {
      const response = await stockService.deleteStockOutput(outputId);
      return { request: { outputId }, deletedOutputIds: response.deletedOutputIds };
    } catch (err: any) {
      return rejectWithValue(err.response.status);
    }
  }
);


interface outputState {
  loading: boolean;
  outputListItems: IOutputListItem[];
  outputToDelete: IOutputListItem | undefined
  totalCost: number;
  totalQuantityAmount: number;
  outputForPrint?: IOutput;
  searchPopover: IPopoverState;
  singleModal: ISingleItemModalProps;
  allItemsModal: boolean;
  returnPopover: IPopoverState;
  editPopover: IPopoverState;
  editDetailsPopover: IPopoverState;
  outputForEdit: IOutput | null;
  error: object;
}

const initialState: outputState = {
  loading: false,
  outputListItems: [],
  outputToDelete: undefined,
  totalCost: 0,
  totalQuantityAmount: 0,
  outputForPrint: undefined,
  searchPopover: { isOpen: false },
  singleModal: { isOpen: false },
  allItemsModal: false,
  returnPopover: { isOpen: false },
  editPopover: { isOpen: false },
  editDetailsPopover: { isOpen: false },
  outputForEdit: null,
  error: {},
};

export const outputSlice = createSlice({
  name: "output",
  initialState,
  reducers: {
    reset: (state) => {
      state = initialState;
    },
    setError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    },
    setSearchPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.searchPopover = action.payload;
    },
    setSingleModal: (state, action: PayloadAction<{ isOpen: boolean, item?: IOutputItem, quantityAmount?: number }>) => {
      state.singleModal.isOpen = action.payload.isOpen;
      state.singleModal.item = action.payload.item;
      state.singleModal.quantityAmount = action.payload.quantityAmount;
    },
    setAllItemsModal: (state, action: PayloadAction<boolean>) => {
      state.allItemsModal = action.payload;
    },
    setReturnPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.returnPopover = action.payload;
    },
    setEditPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.editPopover = action.payload;
    },
    setEditDetailsPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.editDetailsPopover = action.payload;
    },
    updateOutputListItemTemporarily: (
      state,
      action: PayloadAction<IStockOutputUpdate>
    ) => {
      for (let listItemIndex = 0; listItemIndex < state.outputListItems.length; listItemIndex++) {
        if (state.outputListItems[listItemIndex].outputItemId === action.payload.outputItemId) {
          if (action.payload.note !== undefined) {
            state.outputListItems[listItemIndex].note = action.payload.note;
          }
          if (action.payload.inputItemCode !== undefined) {
            state.outputListItems[listItemIndex].code = action.payload.inputItemCode;
          }
        }
      }
    },
    requestDeletingOutput: (state, action: PayloadAction<IOutputListItem | undefined>) => {
      state.outputToDelete = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getOutputListItems.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getOutputListItems.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload) {

        if (action.payload.pagingData.fetchMode === 'replace') {
          const elementsFetched = action.payload.response.listItems.length;
          const newListItems = action.payload.response.listItems;
          const newFirstElementId = newListItems?.[0]?.outputItemId;
          const newLastElementId = newListItems?.[newListItems.length - 1]?.outputItemId;
          state.outputListItems = newListItems;
          action.payload.updateStateAfterFetch({
            fetchSide: "replace",
            newFirstElementId,
            newLastElementId,
            elementsFetched
          });
        } else if (action.payload.pagingData.beforeId) {
          const elementsFetched = action.payload.response.listItems.length;
          const newInputs = [...action.payload.response.listItems, ...state.outputListItems];
          const newFirstElementId = newInputs?.[0]?.outputItemId;
          const newLastElementId = newInputs?.[newInputs.length - 1]?.outputItemId;
          state.outputListItems = newInputs;
          action.payload.updateStateAfterFetch({
            fetchSide: "beforeFirst",
            newFirstElementId,
            newLastElementId,
            elementsFetched
          });
        } else {
          const elementsFetched = action.payload.response.listItems.length;
          const newInputs = [...state.outputListItems, ...action.payload.response.listItems];
          const newFirstElementId = newInputs?.[0]?.outputItemId;
          const newLastElementId = action.payload.response.listItems?.[elementsFetched - 1]?.outputItemId;
          state.outputListItems = newInputs;
          action.payload.updateStateAfterFetch({
            fetchSide: "afterLast",
            newFirstElementId,
            newLastElementId,
            elementsFetched
          });
        }

        state.totalCost = action.payload.response.totalPrice;
        state.totalQuantityAmount = action.payload.response.totalQuantityAmount;
      }
    });
    builder.addCase(getOutputListItems.rejected, (state, action) => {
      state.loading = true;
      state.error = action.error;
    });
    builder.addCase(updateStockOutput.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload) {
        state.outputListItems = state.outputListItems.map(o => {
          if (o.outputId === action.payload.outputId) {
            return {
              ...o,
              project: action.payload.project,
              projectOverride: action.payload.projectOverride,
              createdBy: action.payload.createdBy,
              createdAt: action.payload.createdAt,
              updatedBy: action.payload.updatedBy,
              updatedAt: action.payload.updatedAt,
            };
          } else {
            return o;
          }
        });
        action.payload.items.forEach(updatedItem => {
          state.outputListItems = state.outputListItems.map(o => {
            if (o.outputItemId === updatedItem.outputItemId) {
              return {
                ...o,
                code: updatedItem.itemCode,
                note: action.payload.note
              }
            } else {
              return o;
            }
          });
        })
      }
    });
    builder.addCase(returnOutput.fulfilled, (state, action) => {
      const returnedOutputListItems = action.payload.returnedOutputListItems;
      const updatedOutputItems = action.payload.updatedOutputItems;
      if (returnedOutputListItems) {
        returnedOutputListItems.forEach((outputListItem) => {
          state.outputListItems.unshift(outputListItem);
          state.totalCost += outputListItem.totalCost;
          state.totalQuantityAmount += outputListItem.quantityAmount;
        });
      }
      if (updatedOutputItems) {
        updatedOutputItems.forEach((updatedItem) => {
          const existingItem = state.outputListItems.find(
            (item) => item.outputItemId === updatedItem.outputItemId
          );
          if (existingItem) {
            state.totalCost += updatedItem.totalCost - existingItem.totalCost;
            state.totalQuantityAmount += updatedItem.quantityAmount - existingItem.quantityAmount;
            existingItem.totalCost = updatedItem.totalCost;
            existingItem.quantityAmount = updatedItem.quantityAmount;
          }
        });
      }
    });
    builder.addCase(updateOutputItems.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload) {
        let outputListItemsAfterUpdate: IOutputListItem[] = [];
        let addedUpdatedItems = false;
        for (const outputListItem of state.outputListItems) {
          if (outputListItem.outputId !== action.payload.request.outputId) {
            outputListItemsAfterUpdate.push(outputListItem);
          } else {
            if (!addedUpdatedItems) {
              outputListItemsAfterUpdate = outputListItemsAfterUpdate.concat(action.payload.response.updatedOutputItems);
              addedUpdatedItems = true;
            }
          }
        }
        state.outputListItems = outputListItemsAfterUpdate;
      }
    });
    builder.addCase(getOutputForEdit.fulfilled, (state, action) => {
      state.loading = false;
      state.outputForEdit = action.payload ? action.payload : null;
    });
    builder.addCase(getOutputForPrint.fulfilled, (state, action) => {
      state.loading = false;
      state.outputForPrint = action.payload ? action.payload : undefined;
    });
    builder.addCase(deleteStockOutput.fulfilled, (state, action) => {
      if (action.payload) {
        state.outputListItems = state.outputListItems.filter(outputItem => !action.payload.deletedOutputIds.includes(outputItem.outputId));
      }
    });
  },
});

export const {
  setError,
  setSearchPopover,
  reset,
  setSingleModal,
  setAllItemsModal,
  setReturnPopover,
  setEditPopover,
  setEditDetailsPopover,
  updateOutputListItemTemporarily,
  requestDeletingOutput,
} = outputSlice.actions;

export const selectState = (state: RootState) => state.stock.output;

export default outputSlice.reducer;
